/**
 * ダンジョンデータ変換ユーティリティ
 * 旧形式のダンジョンデータを新形式に変換する
 */

import type { DungeonResult } from '@creanizer/types'

// 旧形式のダンジョン定義（部分的）
interface OldDungeonResult {
  basicInfo: any
  structure: any
  contents: any
  environment?: any // 削除されたフィールド
  exploration?: any // 削除されたフィールド
  story?: any // anecdotesに変更
  gmNotes?: any // 削除されたフィールド
  anecdotes?: any // 新形式
}

/**
 * 旧形式のダンジョンデータを新形式に変換
 */
export function convertOldDungeonToNew(oldDungeon: any): DungeonResult {
  // 既に新形式の場合はそのまま返す
  if (oldDungeon.anecdotes && !oldDungeon.story) {
    return oldDungeon as DungeonResult
  }

  // 基本構造をコピー
  const newDungeon: DungeonResult = {
    basicInfo: {
      ...oldDungeon.basicInfo,
      // recommendedLevelを削除し、featuresがない場合は追加
      features: oldDungeon.basicInfo.features || oldDungeon.basicInfo.warnings || '特記事項なし'
    },
    structure: oldDungeon.structure,
    contents: oldDungeon.contents,
    // storyをanecdotesに変換
    anecdotes: oldDungeon.anecdotes || oldDungeon.story || {
      historyAndLore: '不明',
      rumors: '不明',
      events: '不明',
      hiddenStory: '不明'
    },
    // metadataを追加または維持
    metadata: oldDungeon.metadata || {
      generatedAt: new Date().toISOString(),
      version: '2.0'
    }
  }

  // recommendedLevelを削除
  if ('recommendedLevel' in newDungeon.basicInfo) {
    delete (newDungeon.basicInfo as any).recommendedLevel
  }

  // warningsをfeaturesに変換
  if ('warnings' in newDungeon.basicInfo) {
    newDungeon.basicInfo.features = (newDungeon.basicInfo as any).warnings
    delete (newDungeon.basicInfo as any).warnings
  }

  // 部屋構造の簡略化（必要に応じて）
  if (newDungeon.structure?.floors) {
    newDungeon.structure.floors = newDungeon.structure.floors.map((floor: any) => ({
      ...floor,
      // ambianceがオブジェクトの場合は文字列に変換
      ambiance: typeof floor.ambiance === 'string' 
        ? floor.ambiance 
        : floor.ambiance?.description || 
          (floor.ambiance ? `照明: ${floor.ambiance.lighting || '不明'}, 音: ${floor.ambiance.sounds || '不明'}, 匂い: ${floor.ambiance.smells || '不明'}, 温度: ${floor.ambiance.temperature || '不明'}` : '不明'),
      rooms: floor.rooms?.map((room: any) => ({
        id: room.id,
        name: room.name,
        description: room.description,
        isImportant: room.isImportant || room.type === 'boss' || room.type === 'treasure'
      }))
    }))
  }

  return newDungeon
}

/**
 * ダンジョンデータの配列を変換
 */
export function convertDungeonArray(dungeons: any[]): DungeonResult[] {
  return dungeons.map(dungeon => {
    // SavedDungeonの場合はresultフィールドを変換
    if (dungeon.result) {
      return {
        ...dungeon,
        result: convertOldDungeonToNew(dungeon.result)
      }
    }
    // 直接DungeonResultの場合
    return convertOldDungeonToNew(dungeon)
  })
}

/**
 * インポートデータの検証と変換
 */
export function validateAndConvertDungeonImport(data: any): any {
  // JSON文字列の場合はパース
  const parsedData = typeof data === 'string' ? JSON.parse(data) : data
  
  // 配列形式
  if (Array.isArray(parsedData)) {
    return convertDungeonArray(parsedData)
  }
  
  // オブジェクト形式（dungeons配列を含む）
  if (parsedData.dungeons) {
    return {
      ...parsedData,
      dungeons: convertDungeonArray(parsedData.dungeons)
    }
  }
  
  // データオブジェクト形式（data.dungeonsを含む）
  if (parsedData.data?.dungeons) {
    return {
      ...parsedData,
      data: {
        ...parsedData.data,
        dungeons: convertDungeonArray(parsedData.data.dungeons)
      }
    }
  }
  
  // 単一のダンジョン
  if (parsedData.basicInfo) {
    return convertOldDungeonToNew(parsedData)
  }
  
  return parsedData
}